import { Autocomplete, Backdrop, Box, Button, Card, CardHeader, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, FormHelperText, IconButton, 
  InputAdornment, MenuItem, Select, Stack, TextField as MuiTextField, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import { Identifier, ListContextProvider, useGetMainList, useListContext, useRecordContext, useRefresh } from "ra-core";
import { useCallback, useRef,FC, useMemo } from "react";
import { RecPatch } from "~/dataProvider/tools";
import { EncryptionRuleResponseDto, ExtraIgnoreItem } from "~/gen/api/encrypt";
import { DatabaseType, EditEncryptionMappingInput, EncryptMappingStatus, EncryptionMappingResponseDto, FieldMappingInput, FieldMappingResponseDto, MaskingRule, 
  PrimaryClassification, SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { useMutateEncryptionMappingsContext } from "~/pages/obfuscation/mutateEncryptionMappingsContext";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { FunctionField } from "../../field";
import { Datagrid } from "../../list/datagrid";
import Scrollbar from "../../Scrollbar";
import ClearIcon from '@mui/icons-material/Clear';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Link as RouteLink } from 'react-router-dom'
import { dataTypeDisplayInfo, decideDataTypeMaskingKind } from "../../governance/fieldEdit/maskingRuleDataTypesMap";
import { numericMaskHelperTexts } from "../../governance/fieldEdit/FieldEdit";
import { FormikContextType, FormikProvider, useFormik, useFormikContext } from "formik";
import Create from "~/controller/Create";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import { get, memoize } from 'lodash'
import SelectiveContextProvider from "~/controller/SelectiveContextProvider";
import { usePeekEncryptSettingChangeLocked } from "~/components/obfuscation/usePeekEncryptSettingChange";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "~/constants";
import { disabledColor } from "~/utils/a11y";
import { buildInitialValue, useBigTableMode, useModifiers, validatorBuilder } from "./FieldsMappingList.hooks";
import WithSave from "~/controller/WithSave";


const FieldsMappingList: FC = () => {
  const { currTable, fieldListCtrl, classifications, setFormikCtrl } = useMutateEncryptionMappingsContext()

  const {current: refLookup} = useRef({ 
    formikCtrl: {} as FormikContextType<Record<Identifier, FieldMappingInput>>, 
  })

  const initialValues = useMemo(() => {
    if (fieldListCtrl.loading || fieldListCtrl.ids.length === 0) { return {} }

    return fieldListCtrl.ids.reduce((acc, id) => {
      const field = fieldListCtrl.data[id]
      acc[id] = buildInitialValue(field)
      return acc
    }, {} as Record<Identifier, FieldMappingInput>)
  }, [currTable?.databaseId, currTable?.tableId, fieldListCtrl.loading, fieldListCtrl.ids.length])

  const formikCtrl = useFormik({ 
    initialValues, 
    enableReinitialize: true, 
    onSubmit: () => {}, 
    validate: validatorBuilder(refLookup, classifications as any) 
  })

  setFormikCtrl(formikCtrl)
  refLookup.formikCtrl = formikCtrl
  
  return (
    <ListContextProvider value={fieldListCtrl}>
      <FormikProvider value={formikCtrl}>
        <FieldsMappingListBody />
      </FormikProvider>
    </ListContextProvider>
  )
}


const FieldsMappingListBody: FC = () => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const {
    currTable,
    classifications,
  } = useMutateEncryptionMappingsContext()
  const listCtx = useListContext<FieldMappingResponseDto & RecPatch>()

  const databaseEm : EncryptionMappingResponseDto | undefined = useRecordContext() as any
  const { data: builtInMappingRulesBag } = useGetMainList<EncryptionRuleResponseDto & RecPatch>(
    Number(currTable?.databaseId) > 0 ? 'mapping-rules' : 'noop',
    pages(), sorts(), { dbId: currTable?.databaseId || -1 },
  )
  const builtInMappingRules = useMemo(() => Object.values(builtInMappingRulesBag || {}).sort((a, b) => a.id - b.id), [builtInMappingRulesBag])

  const formikCtx = useFormikContext<Record<Identifier, FieldMappingInput>>()
  const { values, touched, errors, handleChange, handleBlur, setFieldValue, setFieldTouched, setFormikState } = formikCtx

  const {
    synthClassificationValue,
    synthEncryptRuleValue,
    acceptFormChange,
    handleEncryptorChange,
    isRowSelectable,
    discardAllEdits,
    bulkEditMappings,
    mappingEditsCount,
  } = useModifiers(classifications, formikCtx, builtInMappingRulesBag, listCtx)
  const { selectedIds } = listCtx

  const refresh = useRefresh()
  const handlers = useDefaultSavedHandlers("Encryption Mappings Update")
  const peek = usePeekEncryptSettingChangeLocked()
  const encryptSettingChangeLocked = peek(currTable?.databaseId)
  
  const {
    bigTableModeOn,
    SearchInput,
    ListBox,
    popoutOpen,
    editorOnlyListCtx,
    filteredItemCount,
    isStagedField,
    removeFieldFromEditor,
    skipAllSelected,
    resetStagedFields,
  } = useBigTableMode({isFieldMapped, isRowSelectable, bulkEditMappings,
    clearFieldMapping: (field:FieldMappingResponseDto) => {
      setFormikState((s) => {
        delete s.touched[field.fieldId!]
        s.errors && delete s.errors[field.fieldId!]
        return {...s, 
          values: {...s.values, [field.fieldId!]: buildInitialValue(field) },
          touched: {...s.touched},
          errors: {...s.errors},
        }
      })
    },
  })

  const saveMappings = (save: (record: any, callbacks?: any) => void) => () => {
    if (!currTable || peek(currTable.databaseId)) { return }
    if (Object.keys(touched).some(k => !!errors[k])) { return }

    const fieldMappings =
      Object.keys(touched).filter(id => {
        const t = Object.values(touched[id] || {})
        return touched[id] && (t.length === 0 || t.some(v => !!v))
      })
      .map(id => {
        const val = values[id]
        return {
          ...val,
          encryptionRuleId: (val.encryptionRuleId as any) === 'skip' ? null : (val.encryptionRuleId || null),
          maskingRule: val.maskingRule === MaskingRule.ReplaceWithCharacter && (val as any).preserveLast4
            ? MaskingRule.ReplaceWithCharacterPreserveLastFour 
            : val.maskingRule != null ? Number(val.maskingRule) : undefined,
          maskingValue: val.maskingRule ? val.maskingValue : '',
        }
      })
    if (fieldMappings.length === 0) { return }

    const toSave: EditEncryptionMappingInput = {
      databaseId: currTable?.databaseId || 0,
      tableId: currTable?.tableId || 0,
      fieldMappings,
    }
    resetStagedFields()
    save(toSave, {
      onSuccess: () => {
        discardAllEdits()
        refresh()
      }
    })
  }

  const selectorFac = useCallback(memoize((fieldId:number) => (ctx: FormikContextType<any>) => {
    return [
      ctx.values[fieldId],
      ctx.touched[fieldId],
      ctx.errors[fieldId],
    ]
  }), [])

  const theme = useTheme()
  const rowStyles = (d:FieldMappingResponseDto&RecPatch) => {
    if (!bigTableModeOn || !isStagedField(d.fieldId!)) { return undefined }
    
    return { backgroundColor: alpha(theme.palette.success.light, 0.05) };
  }

  const classificationSelectOptions = useMemo(() => {
    return [
      { text: '[None]', value: null, },
      ...classifications,
    ]
  }, [classifications])

  return (
    <Card>
      <CardHeader 
        title={
          !bigTableModeOn ? "Mappings" :
          <Stack component="span" direction="row" spacing={4} sx={{ mr: 'auto' }} alignItems="center">
            <Typography variant="h6">Mappings</Typography>
            <Box component="span" sx={{ border: '1px solid InactiveCaptionText', borderRadius: 0.75, p: 0.5, transform: 'scale(0.7)', lineHeight: '12px' }}>
              <Typography variant="caption" color="InactiveCaptionText">big table mode</Typography>
            </Box>
            {SearchInput}
            {filteredItemCount > 0 ? <Typography variant="caption" color="info.light" sx={{ml:'12px !important'}}>{filteredItemCount}</Typography> : null}
          </Stack>
        } 
        action={
          hasWritePermit && !encryptSettingChangeLocked && mappingEditsCount > 0 ?

          <Stack direction="row" spacing={2}>
            <Create resource="encryption/mappings" basePath="" {...handlers} >
              <WithSave 
                render={({save, saving}) => (
                  <>
                    <Button
                      disabled={saving}
                      variant="outlined"
                      onClick={discardAllEdits}
                    >
                      Cancel
                    </Button>
                    <Button
                      disabled={saving || Object.keys(touched).some(k => !!errors[k])}
                      variant="contained"
                      onClick={saveMappings(save)}
                    >
                      Save
                    </Button>
                  </>
                )}
              />
            </Create>
          </Stack> :
          <Tooltip title="Learn about Encryption Rules">
            <IconButton
              component={RouteLink}
              to="/documents/how-to_guides/encryption/encryption_rules"
              color={"secondary" as any}
            >
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        }
      />
      {bigTableModeOn && popoutOpen &&
        <>
          <Divider />
          <Box>
            <Stack direction="row" alignItems="center" sx={{ mx: 2 }}>
              <Typography variant="subtitle2" sx={{ mr: 'auto' }}>Choose:</Typography>
              <Button onClick={skipAllSelected}>Skip Below Fields</Button>
            </Stack>
            {ListBox}
          </Box>
        </>}
      <Divider />
      <Scrollbar>
        <Box sx={{ minWidth: 1150, position: 'relative' }}>
          {!bigTableModeOn && selectedIds?.length > 0 &&
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'background.paper',
                position: 'absolute',
                width: 'calc(100% - 64px)',
                height: 56,
                left: 52,
                pl: 1.5,
                zIndex: 3,
                '& button': { mr: 2 }
              }}
            >
              <Button
                color="primary"
                variant="outlined"
                onClick={bulkEditMappings}
              >
                Skip
              </Button>
            </Box>
          }
          <Datagrid 
            hasBulkActions={hasWritePermit && !encryptSettingChangeLocked && !bigTableModeOn}
            sx={{ 
              '& th' : { py: 2, '& span': { display: 'inline-block' } },
              '& td' : { py: 2 },
              ...(!hasWritePermit && {
                '& input.Mui-disabled, & div.Mui-disabled': disabledColor,
              })
            }}
            isRowSelectable={!encryptSettingChangeLocked ? isRowSelectable : undefined}
            reversedIndeterminateStateMode={true}
            empty={<Typography variant="subtitle1" textAlign="center" color="InactiveCaptionText" sx={{ my: 1 }}>Empty Mapping</Typography>}
            rowStyle={rowStyles}
            {...(!bigTableModeOn ? listCtx : editorOnlyListCtx)}
          >
            <FunctionField 
              source="fieldName" 
              label={<Box component="span" sx={{ minWidth: 72 }}>Field Name</Box>} 
              sortable={false} 
              render={(d?: FieldMappingResponseDto & RecPatch) => {
                if (!d) { return null}
                if (!isStagedField(d.fieldId!)) { return (<Typography component="span" variant="body2">{d.fieldName}</Typography>)}
                return (
                  <Tooltip title="Remove from editor">
                    <Button
                      onClick={() => removeFieldFromEditor(d.fieldId!)}
                      startIcon={<ClearIcon />}
                      sx={{
                        '& .MuiSvgIcon-root': { opacity: 0, },
                        '&:hover .MuiSvgIcon-root, &:focus .MuiSvgIcon-root': { opacity: 1, },
                      }}
                    >
                      {d.fieldName}
                    </Button>
                  </Tooltip>
                )
              }}
            />
            <FunctionField 
              source="dataType" 
              label={<Box component="span" sx={{ minWidth: 64 }}>Data Type</Box>}
              sortable={false} 
              render={(d?: FieldMappingResponseDto & RecPatch) => (
                d && dataTypeDisplay(d, databaseEm?.databaseType)
              )}
            />
            <FunctionField 
              label={<Box component="span" sx={{ width: 200, display: 'inline-block' }}>Classification</Box>}
              sortable={false}
              render={(d?: FieldMappingResponseDto & RecPatch) => {
                if (!d || !currTable) { return null}
                const fieldId = d.id
                
                const error = get(touched, [d.id]) && get(errors, [d.id, 'secondaryClassificationId'])
                return (
                  <SelectiveContextProvider useContextFn={useFormikContext} ProviderComponent={FormikProvider} selector={(ctx) => [...(selectorFac(d.fieldId || -1)(ctx)), encryptSettingChangeLocked, hasWritePermit]}>

                    <Autocomplete 
                      disabled={!hasWritePermit || encryptSettingChangeLocked}
                      disableClearable={true}
                      options={classificationSelectOptions}
                      getOptionLabel={(option) => option ? (option.text || '') : ''}
                      value={synthClassificationValue(fieldId)}
                      
                      renderInput={((params) =>
                        <MuiTextField 
                          {...params} 
                          fullWidth 
                          size="small" 
                          error={Boolean(error)}
                          helperText={error || undefined}
                          {...{
                            inputProps: {
                              ...params.inputProps,
                              'aria-label': 'Classification',
                            }
                          }}
                        />
                      )}
                      onChange={(event, value, reason) => {
                        if (hasWritePermit && reason === 'selectOption' && d?.fieldId) {
                          if ((value as any)?.value === null) { // choosed '[None]'
                            const patchVal: any = {
                              secondaryClassificationId: undefined,
                              primaryClassification: undefined,
                              isSkipped: true,
                              encryptionRuleId: null,
                              maskingRule: MaskingRule.None,
                              maskingValue: '',
                            }
                            const patchTouched: any = undefined
                            acceptFormChange(d.fieldId, patchVal, patchTouched)
                          } else {
                            const cls1 = value as SecondaryClassificationResponseDto
                            const patchVal: any = {
                              secondaryClassificationId: cls1.secondaryClassificationId,
                              primaryClassification: cls1.primaryClassification,
                              ...((d.isIdentity || d.isComputed || d.extraIgnoreItem !== ExtraIgnoreItem.None) && { isSkipped: true }),
                            }
                            const patchTouched: any =  {
                              secondaryClassificationId: cls1.secondaryClassificationId !== d.secondaryClassificationId
                            }
    
                            const editVal = values[d.fieldId]
    
                            if (cls1.primaryClassification === PrimaryClassification.Public) {
                              patchVal.isSkipped = true
                              patchVal.encryptionRuleId = null
                              patchVal.maskingRule = MaskingRule.None
                              patchVal.maskingValue = ''
                              patchTouched.encryptionRuleId = true
                              patchTouched.maskingRule = true
                            }
                            acceptFormChange(d.fieldId, patchVal, patchTouched)
                          }
                        }
                      }}
                    />
                  </SelectiveContextProvider>
                )
              }}
            />
            <FunctionField 
              source="Encryption Rule" 
              label={<Box component="span" sx={{ width: 200, display: 'inline-block' }}>Encryption Rule</Box>} 
              sortable={false} 
              render={(d?: FieldMappingResponseDto & RecPatch) => {
                if (!d || !currTable) { return null }
                if (d.isIdentity || d.isComputed || d.extraIgnoreItem !== ExtraIgnoreItem.None) {
                  return (
                    <Typography variant="body2">N/A</Typography>
                  )
                }

                const validRuleOptions = builtInMappingRules.filter(r => d?.dataType && r.dataTypes && r.dataTypes.split(',').filter(s => s).includes(d.dataType))
                
                const pcls = 
                  (!values[d.id] || !values[d.id].secondaryClassificationId) ? PrimaryClassification.Public
                  : ((classifications.find(scls => scls.key === values[d.id].secondaryClassificationId) as SecondaryClassificationResponseDto
                    )?.primaryClassification || PrimaryClassification.Public)
                
                const encryptor = values[d.id] && validRuleOptions.find(r => r.id === values[d.id]?.encryptionRuleId)
                const error = get(touched, [d.id]) && get(errors, [d.id, 'encryptionRuleId'])
                return (
                  <SelectiveContextProvider useContextFn={useFormikContext} ProviderComponent={FormikProvider} selector={(ctx) => [...(selectorFac(d.fieldId || -1)(ctx)), validRuleOptions.length, encryptSettingChangeLocked, hasWritePermit]}>

                    <FormControl error={Boolean(error)}>
                      <Select
                        disabled={!hasWritePermit || encryptSettingChangeLocked}
                        value={synthEncryptRuleValue(d)}
                        onChange={handleEncryptorChange(d)}
                        size="small"
                        sx={{ minWidth: 80 }}
                      >
                        <MenuItem value={"skip"}>Skip</MenuItem>
                        {(pcls !== PrimaryClassification.Public)
                        && validRuleOptions.map(r => (
                          <MenuItem key={r.id} value={r as any}>{r.name}</MenuItem>
                        ))}
                      </Select>
                      {Boolean(error) && <FormHelperText id={`${d.id}-encrypt-rule-helper-text`}>{error}</FormHelperText>}
                    </FormControl>
                    {encryptor &&
                      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                        <Typography variant="caption" sx={{ color: 'primary.light', '& span': { width: 44, display: 'inline-block' } }}>
                          <span>Input: </span>{encryptor.sampleInput}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'success.main' }}>Output: {encryptor.sampleOutput}</Typography>
                      </Box>}
                  </SelectiveContextProvider>
                )
              }}
            />
            <FunctionField 
              label={<Box component="span" sx={{ width: 320, display: 'inline-block' }}>Masking Rule</Box>} 
              sortable={false} 
              render={(d?: FieldMappingResponseDto & RecPatch) => {
                if (!d || !currTable) { return null }
                if (d.isIdentity || d.isComputed || d.extraIgnoreItem !== ExtraIgnoreItem.None) {
                  return (
                    <Typography variant="body2">N/A</Typography>
                  )
                }
                const clss = synthClassificationValue(d.id) as SecondaryClassificationResponseDto
                
                const maskingKind = decideDataTypeMaskingKind(d?.dataType || '')
                const mrulePath = `${d?.fieldId}.maskingRule`
                const mvalPath = `${d?.fieldId}.maskingValue`
                const mruleError = get(touched, [d.id]) && get(errors, [d.id, 'maskingRule'])
                const mvalError = get(touched, [d.id]) && get(errors, [d.id, 'maskingValue'])
                return (
                  <SelectiveContextProvider useContextFn={useFormikContext} ProviderComponent={FormikProvider} selector={(ctx) => [...(selectorFac(d.fieldId || -1)(ctx)), encryptSettingChangeLocked, hasWritePermit]}>

                    <FormControl error={Boolean(mruleError)}>
                      <Select
                        disabled={!hasWritePermit || encryptSettingChangeLocked}
                        name={mrulePath}
                        sx={{ minWidth: 120 }}
                        size="small"
                        value={values[d.id]?.maskingRule || (values[d.id]?.maskingRule === MaskingRule.None ? MaskingRule.None : '')}
                        onChange={(e, val) => {
                          setFieldValue(mrulePath, e.target.value)
                          setFieldTouched(mrulePath, 
                            d.maskingRule !== e.target.value ||
                            (d.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour) !== (values[d.id] as any).preserveLast4
                          )
                        }}
                      >
                        <MenuItem value={MaskingRule.None}>None</MenuItem>
                        {clss?.primaryClassification === PrimaryClassification.Public ? null
                          : maskingKind === 'string' ?
                          <MenuItem value={MaskingRule.ReplaceWithCharacter}>Replace With Character</MenuItem>
                          : maskingKind === 'date' ?
                          <MenuItem value={MaskingRule.ReplaceWithDefaultDate}>Replace With Default Date</MenuItem>
                          : (maskingKind === 'integer_num' || maskingKind === 'non_integer_num') ?
                          <MenuItem value={MaskingRule.ReplaceWithDefaultDate}>Replace With Default Value</MenuItem>
                          : null
                        }
                      </Select>
                      {Boolean(mruleError) && <FormHelperText id={`${d.id}-masking-rule-helper-text`}>{mruleError}</FormHelperText>}
                    </FormControl>
                    {values[d.id] && !!values[d.id].maskingRule && (values[d.id].maskingRule || 0) > 0 &&
                      <>
                        {
                          values[d.id].maskingRule !== MaskingRule.ReplaceWithDefaultDate ?
                          <Box sx={{ display: 'flex', flexDirection: 'row', mt: 1 }}>
                            <Box sx={{ width: 110, mr: 2 }}>
                              <span id={`label-c-${d.id}`} className="sr-only">Character</span>
                              <MuiTextField
                                disabled={!hasWritePermit || encryptSettingChangeLocked}
                                select
                                error={!!mvalError}
                                helperText={mvalError || undefined}
                                size="small"
                                name={mvalPath}
                                value={values[d.id] ? (values[d.id].maskingValue || '') : ''}
                                InputProps={{
                                  startAdornment: <InputAdornment aria-label="Character" position="start" sx={{mr:2}}>Char:</InputAdornment>,
                                }}
                                inputProps={{
                                  labelId: `label-c-${d.id}`
                                }}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                {(maskingKind === 'string' ? ['*', '-'] : []).map(s => 
                                  <MenuItem key={s} value={s}>{s}</MenuItem>                          
                                )}
                              </MuiTextField>
                            </Box>
                            <FormControlLabel label="Preserve Last 4" 
                              control={
                                <Checkbox 
                                  disabled={!hasWritePermit || encryptSettingChangeLocked}
                                  checked={(values[d.id] as any).preserveLast4} 
                                  onChange={(e) => { 
                                    setFieldValue(`${d.fieldId}.preserveLast4`, e.target.checked)
                                    setFieldTouched(mrulePath, 
                                      (d.maskingRule === MaskingRule.ReplaceWithCharacterPreserveLastFour) !== e.target.checked
                                    )
                                  }} 
                                />
                              } 
                            />
                          </Box> :
                          <>
                            <label htmlFor={`label-mv-${d.id}`} className="sr-only">Masking Value</label>
                            <MuiTextField 
                              disabled={!hasWritePermit || encryptSettingChangeLocked}
                              id={`label-mv-${d.id}`} 
                              sx={{ mt: 1, maxWidth: 230 }}
                              size="small"
                              name={mvalPath}
                              helperText={
                                maskingKind === 'date' ? "Date in format like: 1970-01-01"
                                : ((maskingKind === 'integer_num' || maskingKind === 'non_integer_num') 
                                  ? (numericMaskHelperTexts[(d.dataType || '').toLowerCase()] || errors[d.id]?.maskingValue)
                                  : errors[d.id]?.maskingValue
                                  )
                              }
                              InputProps={{
                                "aria-label": 'Masking Value'
                              }}
                              autoComplete="off"
                              error={!!mvalError}
                              value={values[d.id] ? (values[d.id].maskingValue || '') : ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          </>
                        }
                      </>
                    }
                  </SelectiveContextProvider>
              )}}
            />
          </Datagrid>
        </Box>
      </Scrollbar>
      <Backdrop open={listCtx.loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    </Card>
  )
}


const isFieldMapped = (f:FieldMappingResponseDto) => f.encryptMappingStatus === EncryptMappingStatus.Mapped || Boolean(f.maskingRule)

const dataTypeDisplay = (d:FieldMappingResponseDto, dbType?: DatabaseType) => {
  const info = dataTypeDisplayInfo(d, dbType)
  return (
    <Typography variant="body2"
      sx={{
        '& div': { transform: 'scale(0.8) translateX(-12px)', },
        '& span.MuiTypography-caption': { mr: 0.5, },
      }}
    >
      {(d.isPrimaryKey || d.isForeignKey || d.isIdentity || d.isComputed || d.extraIgnoreItem !== ExtraIgnoreItem.None) && <Box>
        {d.isPrimaryKey && <Typography variant="caption" color="primary">[PK]</Typography>}
        {d.isForeignKey && <Typography variant="caption" color="secondary">[FK]</Typography>}
        {d.isIdentity && <Typography variant="caption" color="InactiveCaptionText">[Identity]</Typography>}
        {d.isComputed && <Typography variant="caption" color="InactiveCaptionText">[Computed]</Typography>}
        {d.extraIgnoreItem === ExtraIgnoreItem.TransitiveIdentity && <Typography variant="caption" color="InactiveCaptionText">[Identity - transitive]</Typography>}
        <br />
      </Box>}
      {info.dataType}{info.dataTypeExt}
    </Typography>
  )
}


export default FieldsMappingList;
